import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { inviteLinkKeys } from "@/lib/query-keys";
import type { InviteLink, LinkType, PaginatedResponse } from "@/types/api";

export function useInviteLinks(projectId: string, enabled: boolean) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useQuery({
    queryKey: inviteLinkKeys.byProject(projectId),
    queryFn: async () => {
      const data = await fetchWithAuth<PaginatedResponse<InviteLink>>(
        `/projects/1.0/projects/${projectId}/invite-links/`,
      );
      return data.results;
    },
    enabled,
  });
}

export function useCreateInviteLink(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (linkType: LinkType) => {
      return fetchWithAuth<InviteLink>(
        `/projects/1.0/projects/${projectId}/invite-links/`,
        {
          method: "POST",
          body: JSON.stringify({ link_type: linkType }),
        },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: inviteLinkKeys.byProject(projectId),
      });
    },
  });
}

export function useDeleteInviteLink(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (linkType: LinkType) => {
      return fetchWithAuth<void>(
        `/projects/1.0/projects/${projectId}/invite-links/${linkType}/`,
        { method: "DELETE" },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: inviteLinkKeys.byProject(projectId),
      });
    },
  });
}
